// BoxAlong — equity position computations from a buy/sell trade log.
export const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };

export const PERIODS = [
  { key: "year", label: "Calendar year" },
  { key: "56w", label: "56-week running" },
  { key: "90d", label: "90-day" },
  { key: "180d", label: "180-day" },
];

function inWindow(dateStr, period) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  if (period === "year") return d.getFullYear() === now.getFullYear();
  const days = period === "56w" ? 392 : period === "90d" ? 90 : period === "180d" ? 180 : 0;
  const cutoff = new Date(now.getTime() - days * 86400000);
  return d >= cutoff && d <= now;
}

// trades: [{ side:'buy'|'sell', units, price, date }] for one position.
export function computePosition(trades, currentPrice, period = "year") {
  const sorted = [...(trades || [])].sort((a, b) =>
    String(a.date || "").localeCompare(String(b.date || "")) ||
    String(a.created_at || "").localeCompare(String(b.created_at || ""))
  );
  const buys = sorted.filter((t) => t.side === "buy");
  const sells = sorted.filter((t) => t.side === "sell");

  const totalBuyUnits = buys.reduce((s, t) => s + n(t.units), 0);
  const totalBuyCost = buys.reduce((s, t) => s + n(t.units) * n(t.price), 0);
  const totalSellUnits = sells.reduce((s, t) => s + n(t.units), 0);
  const totalSellProceeds = sells.reduce((s, t) => s + n(t.units) * n(t.price), 0);

  const avgPurchasePrice = totalBuyUnits ? totalBuyCost / totalBuyUnits : 0;
  const avgSalePrice = totalSellUnits ? totalSellProceeds / totalSellUnits : 0;
  const lastBuy = buys[buys.length - 1];
  const lastSell = sells[sells.length - 1];

  // running average-cost basis of units still on hand
  let avg = 0, units = 0;
  for (const t of sorted) {
    if (t.side === "buy") {
      const u = n(t.units);
      avg = units + u ? (units * avg + u * n(t.price)) / (units + u) : 0;
      units += u;
    } else {
      units -= n(t.units);
    }
  }
  const unitsOnHand = totalBuyUnits - totalSellUnits;
  const avgCarryingPrice = avg;
  const curCarry = currentPrice != null && currentPrice !== "" ? n(currentPrice) : avgCarryingPrice;

  const purchasedPeriod = buys.filter((t) => inWindow(t.date, period)).reduce((s, t) => s + n(t.units), 0);
  const soldPeriod = sells.filter((t) => inWindow(t.date, period)).reduce((s, t) => s + n(t.units), 0);

  return {
    avgPurchasePrice,
    lastPurchasePrice: lastBuy ? n(lastBuy.price) : 0,
    lastUnitsPurchased: lastBuy ? n(lastBuy.units) : 0,
    avgCarryingPrice,
    currentCarryingPrice: curCarry,
    unitsCarried: unitsOnHand,
    avgSalePrice,
    lastSalePrice: lastSell ? n(lastSell.price) : 0,
    lastUnitsSold: lastSell ? n(lastSell.units) : 0,
    numSoldPeriod: soldPeriod,
    numPurchasedPeriod: purchasedPeriod,
    numOnHand: unitsOnHand,
    valueSold: avgSalePrice * soldPeriod,
    valueBought: avgPurchasePrice * purchasedPeriod,
    valueHeld: curCarry * unitsOnHand,
    tradeCount: sorted.length,
  };
}

export const fmt = (v, dp = 2) => (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
export const fmtu = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
